/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
import { IHeaderViewProps } from '@msdyn365-commerce-modules/header/src/modules/header/./header';

const headerView: React.FC<IHeaderViewProps  & IHeaderProps<{}>> = (props: IHeaderViewProps & IHeaderProps<{}>) => {
    const {
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        MobileMenuContainer,
        MobileMenuBodyContainer,
        MobileMenuLinksContainer,
        Divider
    } = props;
    return (
        <Module {...HeaderTag}>
            <Node {...HeaderContainer}>
                <Node {...HeaderTopBarContainer}>
                    {props.preferredStore}
                    {_renderDesktopAccountBlock(props)}
                    {props.wishListIconDesktop}
                    {props.cartIcon}
                    <Node {...Divider} />
                    {_renderReactFragment(props.siteOptions)}
                </Node>
                <div className="main_bar">
                    {_buildLogo()}
                    {_renderReactFragment(props.menuBar)}
                    {props.navIcon}
                    {_renderReactFragment(props.search)}
                </div>
                <Node {...MobileMenuContainer}>
                    <Node {...MobileMenuBodyContainer}>
                        { props.MobileMenuHeader }
                        {_renderReactFragment(props.menuBar)}
                        <Node {...MobileMenuLinksContainer}>
                            { props.accountLinks ? props.accountLinks.map(link => link) : false }
                            { props.siteOptions }
                            { props.wishListIconMobile }
                            { props.signInLink }
                            { props.signOutLink }
                        </Node>
                    </Node>
                </Node>
            </Node>
        </Module>
    );
};

function _renderDesktopAccountBlock(props: IHeaderViewProps): JSX.Element | null {
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;

    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        { accountLinks ? accountLinks.map(link => link) : false }
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {signInLink}
                </Node>
            );
        }
    }
    props.context.telemetry.error('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0 ? items.map((slot: React.ReactNode, index: number) => {
                return (<React.Fragment key={index}>
                    {slot}
                </React.Fragment>);
            }) : null}
        </>
    );
}

//Moved this to a function to clean up the render code above
function _buildLogo(): JSX.Element{
    return <div className="logo"><a href="/" aria-label="Return to homepage"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 580 80"><path style={{fill:"#11175e"}} d="M141.6 28.4V76H134l-22.4-32.4h-.4V76h-8.6V28.4h7.7l22.3 32.4h.4V28.4h8.6zM190 28.4V76h-7.6L160 43.6h-.4V76H151V28.4h7.7L181 60.8h.5V28.4h8.5zM70.5 68.7c-4-.5-7.4-3-9.5-7.4-2.2-5-2.2-13.2 0-18.2a12 12 0 0 1 9.5-7.4v-7.8C58 29 50.8 39 50.8 52.2s7.2 23.3 19.8 24.3v-7.7ZM91.5 39a20.2 20.2 0 0 0-17-11.1v7.8c8 1 11.2 8.4 11.2 16.5s-3.2 15.4-11.2 16.6v7.7c7.6-.6 13.5-4.5 17-11.2 3.9-7.3 3.9-19 0-26.3ZM34.9 61.8c-1.6 4.6-5.9 7-11 7s-9.1-2.4-11.4-7.4c-2.3-5-2.4-13.2 0-18.3 2.3-5 6.5-7.6 11.5-7.6 4.1 0 7.8 1.6 10 5.2l.3.8h9a18.2 18.2 0 0 0-12-12.6 23.4 23.4 0 0 0-18.6 1.8c-6 3.6-10.6 10.6-10.6 21.5s4.5 18 10.6 21.5a23.3 23.3 0 0 0 18.9 1.7c5.9-2.2 10.5-6.8 12-13.6h-8.8Z"/><path d="M207.4 69.1 201 31.5l.4-.1c4 8.3 8 16.5 12.6 25.2l5.2-27h.4l3.8 35.8-8.8-4.9c-2.4 2.8-4.8 5.5-7 8.3l-.2.4ZM225 65 223 29h.3l10 24.8 7.5-25.6h.4l1.6 35.4-.3-.2-8.7-5.7c-3 2.3-5.8 4.6-8.5 6.9l-.3.3ZM241.2 25.5l-4.3-7.1L242.3 7l.4.1-1.5 18.4zM279.8 69.1l-.3-.3a203 203 0 0 0-7-8.3l-8.7 4.9 3.8-35.8h.3c2 9.5 3.6 18.3 5.3 27 4.5-8.7 8.6-17 12.5-25.2h.4l-6.3 37.7ZM262.1 65l-.3-.3c-2.7-2.3-5.5-4.6-8.5-6.9l-8.7 5.7-.3.2 1.6-35.4h.4l7.5 25.6 10-24.8h.4L262 65ZM246 25.4l-1.5-18.3.3-.1 5.5 11.4-4.3 7Z" style={{fill:"#aa872e"}}/><path d="M278.7 76h-.2a112 112 0 0 0-69.7 0h-.3l-.8-5.6h.2c23.4-7.3 48.2-7.3 71.7 0h.1l-1 5.6Z" style={{fill:"#aa872e"}}/><path d="M329.8 53.3c-8-6-20.3-4.3-23.9-9.5-1-1.5-1-3.9.4-5.7 2-2.3 5.4-3 8.4-3 4.7 0 9.4 1.7 10 6.4h9.1c-.3-18.5-38.3-18.2-38.3.3a10 10 0 0 0 3.7 8.2c7.5 6 20.2 5 24.6 9.5 1.7 1.8 1.5 4.7-.2 6.6-2.1 2.4-5.9 3.2-9.1 3.2-5.4 0-10.6-2-11-7.4H294c.3 6.4 3.8 10.8 9.8 13a33.7 33.7 0 0 0 21.4 0c5.6-2.2 9.2-6.3 9.2-12.3 0-4-1.7-7-4.7-9.3ZM352.6 55.8h23v-7.2h-23V35.7h24.7v-7.3h-34.2V76h34.4v-7.3h-24.9V55.8zM396.6 28.4h-9.5V76h32.8v-7.3h-23.3V28.4zM456.1 62.9h-.7l-15.6-34.5h-11.7V76h9.2V43.2h.5l14.5 32.6h6.9l14.5-32.5h.5V76h9.2V28.4h-11.7l-15.6 34.5zM503.3 55.8h23v-7.2h-23V35.7h24.8v-7.3h-34.3V76h34.5v-7.3h-25V55.8zM567.4 76h10.7L566.7 57l1-.4c2.8-1.2 4.9-3 6.3-5.1a15.8 15.8 0 0 0 0-16c-3.4-5.4-10-7.2-16.4-7.2h-19.8V76h9.6V58.5h9.7L567.5 76Zm-20-40.4c8.6 0 11.7-.2 14.6 1 3 1.2 4.4 3.7 4.4 7s-1.4 5.7-4.4 7c-3 1-6.3.8-14.6.8V35.6Z" style={{fill:"#11175e"}}/></svg></a></div>
}

export default headerView;